<template>
    <BaseDialog
        :showDialog="show"
        maxDialogWidth="1100"
        maxCardHeight="400"
        :headline="headline"
        :close="onClose"
        :alvqa="alvqa"
    >
        <template v-slot:dialogText>
            <v-data-table
                :headers="headers"
                :items="rows"
                :loading="isLoading"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                hide-default-footer
                :items-per-page="-1"
                class="gema-repertoire-table"
            >
                <template v-slot:progress>
                    <v-progress-linear
                        color="dialogProgressbar"
                        height="2"
                        indeterminate
                    />
                </template>

                <template v-slot:item.title="{ item }">
                    <BaseTooltip v-if="item.title">
                        <template v-slot:content>
                            <span class="gema-ellipsis">{{ item.title }}</span>
                        </template>
                        <template v-slot:text>{{ item.title }}</template>
                    </BaseTooltip>
                    <span v-else>&nbsp;</span>
                </template>

                <template v-slot:item.composers="{ item }">
                    <BaseTooltip v-if="item.composers">
                        <template v-slot:content>
                            <span class="gema-ellipsis">{{ item.composers }}</span>
                        </template>
                        <template v-slot:text>{{ item.composers }}</template>
                    </BaseTooltip>
                    <span v-else>&nbsp;</span>
                </template>

                <template v-slot:item.publishers="{ item }">
                    <BaseTooltip v-if="item.publishers">
                        <template v-slot:content>
                            <span class="gema-ellipsis">{{ item.publishers }}</span>
                        </template>
                        <template v-slot:text>{{ item.publishers }}</template>
                    </BaseTooltip>
                    <span v-else>&nbsp;</span>
                </template>

                <template v-slot:item.workVersionNumber="{ item }">
                    <span class="gema-nowrap">{{ item.workVersionNumber }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                    <span class="gema-nowrap">{{ item.status }}</span>
                </template>

                <template v-slot:item.inAlv="{ item }">
                    <template v-if="item.inAlv !== null">
                        <BaseIcon
                            v-if="item.inAlv"
                            icon="check"
                            color="success"
                            :size="16"
                        />
                        <BaseIcon
                            v-else
                            icon="close"
                            color="error"
                            :size="16"
                        />
                    </template>
                </template>

                <template v-slot:no-data>
                    <div class="text-center">
                        {{ $t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND') }}
                    </div>
                </template>
            </v-data-table>
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseIcon from '@/components/_buttons/BaseIcon'

export default {
    name: 'GemaRepertoireDialog',
    components: {
        BaseDialog,
        BaseTooltip,
        BaseIcon,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        workId: {
            type: String,
            default: '',
        },
        alvqa: {
            type: String,
            default: 'gema-repertoire-dialog',
        },
    },
    data() {
        return {
            isLoading: false,
            gemaVersionsExistence: {},
            loadToken: 0,
        }
    },
    computed: {
        baseWorkNumber() {
            const fullId = this.workId || ''
            const match = typeof fullId === 'string'
                ? fullId.match(/^(\d{1,10})(?:-\d{3})?$/)
                : null
            return match ? match[1] : fullId
        },
        headline() {
            return this.$t('lang_PUBLISHING_WORKS_CAPTION_GEMA_REPERTOIRE_DIALOG_TITLE', {
                workNumber: this.baseWorkNumber,
            })
        },
        repertoireVersions() {
            return this.$store.getters.getGemaRepertoireVersions
        },
        headers() {
            return [
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_WORK_TITLE'),
                    value: 'title',
                    align: 'left',
                    width: '28%',
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_COMPOSERS'),
                    value: 'composers',
                    align: 'left',
                    width: '24%',
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_PUBLISHERS'),
                    value: 'publishers',
                    align: 'left',
                    width: '24%',
                },
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_VERSION'),
                    value: 'workVersionNumber',
                    align: 'left',
                    width: '10%',
                },
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_STATUS'),
                    value: 'status',
                    align: 'left',
                    width: '8%',
                },
                {
                    text: '',
                    value: 'inAlv',
                    align: 'center',
                    width: '40px',
                    sortable: false,
                },
            ]
        },
        rows() {
            const data = this.repertoireVersions
            if (!data) return []

            const items = Array.isArray(data.works)
                ? data.works
                : Array.isArray(data.results)
                    ? data.results
                    : []

            return items.map(it => {
                const ids = it.identifiers || {}
                const writers = Array.isArray(it.writers) ? it.writers : []
                const composerNames = writers
                    .filter(w => w.roleCode === 'COMPOSER' || w.roleCode === 'COMPOSER_AUTHOR')
                    .map(w => {
                        let baseName = ''
                        if (w.name) {
                            baseName = w.name
                        } else {
                            const parts = []
                            if (w.firstName) parts.push(w.firstName)
                            if (w.lastName) parts.push(w.lastName)
                            baseName = parts.join(' ')
                        }
                        if (!baseName) return ''

                        let roleSuffix = ''
                        if (w.roleCode === 'COMPOSER') {
                            roleSuffix = ' (C)'
                        } else if (w.roleCode === 'COMPOSER_AUTHOR') {
                            roleSuffix = ' (CA)'
                        }

                        return baseName + roleSuffix
                    })
                    .filter(Boolean)

                const originalPublishers = Array.isArray(it.originalPublishers) ? it.originalPublishers : []
                const subPublishers = Array.isArray(it.subPublishers) ? it.subPublishers : []
                const publisherNames = [
                    ...originalPublishers.map(p => p.name ? p.name + ' (E)' : ''),
                    ...subPublishers.map(p => p.name ? p.name + ' (SE)' : ''),
                ].filter(Boolean)

                const fullVersion = ids.workVersionNumber || ids.work_version_number || ''
                let shortVersion = fullVersion
                if (typeof fullVersion === 'string' && fullVersion.indexOf('-') !== -1) {
                    const parts = fullVersion.split('-')
                    shortVersion = parts[parts.length - 1] || fullVersion
                }

                const hasKey = Object.prototype.hasOwnProperty.call(this.gemaVersionsExistence, fullVersion)
                const inAlvValue = hasKey ? this.gemaVersionsExistence[fullVersion] : null

                return {
                    workNumber: ids.workNumber || ids.work_number || '',
                    workVersionNumber: shortVersion,
                    fullVersionNumber: fullVersion,
                    title: it.originalTitle || it.title || it.workTitle || '',
                    composers: composerNames.join(', '),
                    publishers: publisherNames.join(', '),
                    status: it.status != null ? it.status : '',
                    inAlv: inAlvValue,
                }
            })
        },
    },
    watch: {
        show(val) {
            if (val) this.load()
        },
    },
    methods: {
        async load() {
            if (!this.workId) return

            const token = ++this.loadToken
            this.isLoading = true
            this.gemaVersionsExistence = {}

            try {
                await this.$store.dispatch('fetchGemaRepertoireVersions', {
                    workNumber: this.baseWorkNumber,
                })
                if (token !== this.loadToken || !this.show) return

                const versions = this.repertoireVersions
                const items = versions && Array.isArray(versions.works)
                    ? versions.works
                    : versions && Array.isArray(versions.results)
                        ? versions.results
                        : []

                if (items.length > 0) {
                    const fullVersionNumbers = items
                        .map(work => {
                            const ids = work.identifiers || {}
                            return ids.workVersionNumber || ids.work_version_number || ''
                        })
                        .filter(Boolean)

                    if (fullVersionNumbers.length > 0) {
                        const existenceResult = await this.$store.dispatch('checkGemaVersionsExistence', {
                            workVersions: fullVersionNumbers,
                        })
                        if (token !== this.loadToken || !this.show) return
                        if (existenceResult) {
                            this.gemaVersionsExistence = existenceResult
                        }
                    }
                }
            } finally {
                if (token === this.loadToken) {
                    this.isLoading = false
                }
            }
        },
        onClose() {
            this.loadToken++
            this.isLoading = false
            this.gemaVersionsExistence = {}
            this.$store.commit('setGemaRepertoireVersions', null)
            this.$emit('close')
        },
    },
}
</script>

<style>
    .gema-repertoire-table {
        table-layout: fixed;
        width: 100%;
    }
    .gema-repertoire-table th,
    .gema-repertoire-table td {
        white-space: nowrap;
        padding-left: 12px !important;
        padding-right: 12px !important;
    }
    .gema-repertoire-table .v-data-table__wrapper {
        overflow-x: hidden !important;
    }
    .gema-repertoire-table table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
    }
    .gema-ellipsis {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .gema-nowrap {
        white-space: nowrap;
    }
</style>
